import * as d3 from "d3"
import { useRef, useEffect, useState } from "react";


const PieChart = ({data, targetKey}) => {
  const chartRef = useRef();
  const [formattedData, setFormattedData] = useState([])

  useEffect(() => {
    let formatted = Object.values(data.reduce((acc, curr) => {
      if (acc[curr[targetKey]]) {
        acc[curr[targetKey]].value += 1
      } else {
        acc[curr[targetKey]] = {name: curr[targetKey], value: 1}
      }
      return acc
    }, {}))

    formatted.forEach(el => {
      el.percentage = Math.round(el.value / data.length * 10000) / 100
    })

    setFormattedData(formatted)
  },[data, targetKey])

  useEffect(() => {
    const width = 1200;
    const height = 800;
    const radius = Math.min(width, height) / 3;
    const startAngle = Math.PI / 3.5;   // Slightly above the middle of the right side (30 degrees)
    const endAngle = Math.PI * 2 + Math.PI / 3.5; // Completes the circle back to the starting point
    

    // Clear previous chart if any
    d3.select(chartRef.current).selectAll("*").remove();

    // Create an SVG container
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const pie = d3.pie().sort((a,b) => {
      return a.value-b.value // sort by prevelance
    }).value(d => d.value).startAngle(startAngle).endAngle(endAngle);
    const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius * 0.8);
    const arcLabel = d3.arc().innerRadius(radius * 1.1).outerRadius(radius * 1.1);

    const pieData = pie(formattedData);

    // Function to handle hover effect
    function handleHover(event, d) {
      svg.selectAll("path")
        .transition()
        .style("opacity", slice => (slice === d ? 1 : 0.3));

      svg.selectAll("polyline")
        .transition()
        .style("opacity", line => (line === d ? 1 : 0.3));

      svg.selectAll("text")
        .transition()
        .style("opacity", label => (label === d ? 1 : 0.3));
    }

    // Function to reset opacity after hover
    function handleMouseOut() {
      svg.selectAll("path")
        .transition()
        .style("opacity", 1);
      svg.selectAll("polyline")
        .transition()
        .style("opacity", 1);
      svg.selectAll("text")
        .transition()
        .style("opacity", 1);
    }

    // Draw the donut slices with hover events
    svg.selectAll("path")
      .data(pieData)
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.name))
      .attr("stroke", "white")
      .style("stroke-width", "2px")
      .on("mouseenter", handleHover)
      .on("mouseleave", handleMouseOut);

    // Add lines connecting slices to labels
    svg.selectAll("polyline")
      .data(pieData)
      .enter()
      .append("polyline")
      .attr("stroke", "black")
      .style("fill", "none")
      .attr("stroke-width", 1)
      .attr("points", (d, i) => {
        const posA = arc.centroid(d); // Line start point (slice edge)
        const posB = arcLabel.centroid(d); // Line end point (outside slice)
        
        const angle = (d.startAngle + d.endAngle) / 2;
        const posC = [...posB];
        posC[0] = radius * 1.1 * (angle < Math.PI ? 1 : -1);

        return [posA, posB, posC];
      })
      .on("mouseenter", handleHover)
      .on("mouseleave", handleMouseOut);

    // Add annotations (labels)
    svg.selectAll("text")
      .data(pieData)
      .enter()
      .append("text")
      .attr("transform", d => {
        const pos = arcLabel.centroid(d);
        const angle = (d.startAngle + d.endAngle) / 2;
        pos[0] = radius * 1.15 * (angle < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      })
      .attr("text-anchor", d => {
        const angle = (d.startAngle + d.endAngle) / 2;
        return angle < Math.PI ? "start" : "end";
      })
      .style("font-size", "12px")
      .text(d => `${d.data.name} (${d.data.percentage}%)`)
      .on("mouseenter", handleHover)
      .on("mouseleave", handleMouseOut);

  }, [formattedData]);

  return <div style={{margin: "auto", width: "fit-content"}} ref={chartRef}></div>;
};

export default PieChart;
